import { FC, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import { NioshLiftEvaluationDto } from "src/app/services/generatedApi";
import { MeasurementSystem, getUnits } from "src/utils/measurementConversions";

interface NioshAssessmentTableProps {
  originData: NioshLiftEvaluationDto;
  destinationData: NioshLiftEvaluationDto;
  onOriginUpdate: (newData: NioshLiftEvaluationDto) => void;
  onDestinationUpdate: (newData: NioshLiftEvaluationDto) => void;
  measurementSystem: MeasurementSystem;
  results?: {
    origin: {
      rwl: number;
      liftingIndex: number;
      multipliers: any;
    };
    destination: {
      rwl: number;
      liftingIndex: number;
      multipliers: any;
    };
  };
}

export const NioshAssessmentTable: FC<NioshAssessmentTableProps> = ({
  originData,
  destinationData,
  onOriginUpdate,
  onDestinationUpdate,
  measurementSystem,
  results,
}) => {
  const units = getUnits(measurementSystem);

  const verticalTravelDistance = useMemo(() => {
    return Math.abs(
      originData.verticalHeightIn - destinationData.verticalHeightIn,
    );
  }, [originData.verticalHeightIn, destinationData.verticalHeightIn]);

  const handleOriginChange =
    (field: keyof NioshLiftEvaluationDto) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const numericValue = field === "coupling" ? value : Number(value);
      onOriginUpdate({
        ...originData,
        [field]: numericValue,
      });
    };

  const handleDestinationChange =
    (field: keyof NioshLiftEvaluationDto) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const value = event.target.value;
      const numericValue = field === "coupling" ? value : Number(value);
      onDestinationUpdate({
        ...destinationData,
        [field]: numericValue,
      });
    };

  const handleOriginSelectChange =
    (field: keyof NioshLiftEvaluationDto) => (event: any) => {
      onOriginUpdate({
        ...originData,
        [field]: event.target.value,
      });
    };

  const handleDestinationSelectChange =
    (field: keyof NioshLiftEvaluationDto) => (event: any) => {
      onDestinationUpdate({
        ...destinationData,
        [field]: event.target.value,
      });
    };

  const formatMultiplier = (value?: number) => {
    return value ? value.toFixed(3) : "—";
  };

  const formatResultWeight = (value?: number) => {
    return value ? `${value} ${units.weightAbbr}` : "—";
  };

  return (
    <TableContainer component={Paper} elevation={1}>
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>
              Parameter
            </TableCell>
            <TableCell
              align="center"
              sx={{ fontWeight: "bold", bgcolor: "primary.100" }}
            >
              Origin
            </TableCell>
            <TableCell
              align="center"
              sx={{ fontWeight: "bold", bgcolor: "primary.100" }}
            >
              Destination
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell></TableCell>
            <TableCell
              align="center"
              sx={{
                fontWeight: "medium",
                bgcolor: "primary.50",
                fontSize: "0.85rem",
              }}
            >
              Measurement | Multiplier
            </TableCell>
            <TableCell
              align="center"
              sx={{
                fontWeight: "medium",
                bgcolor: "primary.50",
                fontSize: "0.85rem",
              }}
            >
              Measurement | Multiplier
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          <TableRow>
            <TableCell sx={{ fontWeight: "medium" }}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Load Weight (L)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {units.weight}
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={originData.loadWeightLb}
                    onChange={handleOriginChange("loadWeightLb")}
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ bgcolor: "white" }}
                  />
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  —
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={destinationData.loadWeightLb}
                    onChange={handleDestinationChange("loadWeightLb")}
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ bgcolor: "white" }}
                  />
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  —
                </Box>
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={{ fontWeight: "medium" }}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Load Constant
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {units.weight}
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                  py={2}
                >
                  {measurementSystem === "US" ? "51" : "23"}
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  LC = {measurementSystem === "US" ? "51" : "23"}
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                  py={2}
                >
                  {measurementSystem === "US" ? "51" : "23"}
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  LC = {measurementSystem === "US" ? "51" : "23"}
                </Box>
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={{ fontWeight: "medium" }}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Horizontal Location (H)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {units.length}
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={originData.horizontalDistanceIn}
                    onChange={handleOriginChange("horizontalDistanceIn")}
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ bgcolor: "white" }}
                  />
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  HM = {formatMultiplier(results?.origin.multipliers.hm)}
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={destinationData.horizontalDistanceIn}
                    onChange={handleDestinationChange("horizontalDistanceIn")}
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ bgcolor: "white" }}
                  />
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  HM = {formatMultiplier(results?.destination.multipliers.hm)}
                </Box>
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={{ fontWeight: "medium" }}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Vertical Location (V)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {units.length}
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={originData.verticalHeightIn}
                    onChange={handleOriginChange("verticalHeightIn")}
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ bgcolor: "white" }}
                  />
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  VM = {formatMultiplier(results?.origin.multipliers.vm)}
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={destinationData.verticalHeightIn}
                    onChange={handleDestinationChange("verticalHeightIn")}
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ bgcolor: "white" }}
                  />
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  VM = {formatMultiplier(results?.destination.multipliers.vm)}
                </Box>
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={{ fontWeight: "medium" }}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Vertical Travel Distance (|Vo - Vd|)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {units.length}
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                  py={2}
                >
                  <Typography variant="body2" sx={{ color: "orange.800" }}>
                    {verticalTravelDistance.toFixed(1)}
                  </Typography>
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  DM = {formatMultiplier(results?.origin.multipliers.dm)}
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                  py={2}
                >
                  <Typography variant="body2" sx={{ color: "orange.800" }}>
                    {verticalTravelDistance.toFixed(1)}
                  </Typography>
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  DM = {formatMultiplier(results?.destination.multipliers.dm)}
                </Box>
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={{ fontWeight: "medium" }}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Asymmetry Angle (A)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {units.angle}
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={originData.asymmetryAngleDeg}
                    onChange={handleOriginChange("asymmetryAngleDeg")}
                    inputProps={{ min: 0, max: 135, step: 1 }}
                    sx={{ bgcolor: "white" }}
                  />
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  AM = {formatMultiplier(results?.origin.multipliers.am)}
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={destinationData.asymmetryAngleDeg}
                    onChange={handleDestinationChange("asymmetryAngleDeg")}
                    inputProps={{ min: 0, max: 135, step: 1 }}
                    sx={{ bgcolor: "white" }}
                  />
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  AM = {formatMultiplier(results?.destination.multipliers.am)}
                </Box>
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={{ fontWeight: "medium" }}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Lifting Duration
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  category
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <FormControl fullWidth size="small">
                    <Select
                      value={originData.durationCategory}
                      onChange={handleOriginSelectChange("durationCategory")}
                      sx={{ bgcolor: "white" }}
                    >
                      <MenuItem value={1}>≤ 1 hour</MenuItem>
                      <MenuItem value={2}>1-2 hours</MenuItem>
                      <MenuItem value={3}>&gt; 2 hours</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  —
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <FormControl fullWidth size="small">
                    <Select
                      value={destinationData.durationCategory}
                      onChange={handleDestinationSelectChange(
                        "durationCategory",
                      )}
                      sx={{ bgcolor: "white" }}
                    >
                      <MenuItem value={1}>≤ 1 hour</MenuItem>
                      <MenuItem value={2}>1-2 hours</MenuItem>
                      <MenuItem value={3}>&gt; 2 hours</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  —
                </Box>
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={{ fontWeight: "medium" }}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Lifting Frequency (F)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {units.frequency}
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={originData.liftFrequencyPerMin}
                    onChange={handleOriginChange("liftFrequencyPerMin")}
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ bgcolor: "white" }}
                  />
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  FM = {formatMultiplier(results?.origin.multipliers.fm)}
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <TextField
                    fullWidth
                    size="small"
                    type="number"
                    value={destinationData.liftFrequencyPerMin}
                    onChange={handleDestinationChange("liftFrequencyPerMin")}
                    inputProps={{ min: 0, step: 0.1 }}
                    sx={{ bgcolor: "white" }}
                  />
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  FM = {formatMultiplier(results?.destination.multipliers.fm)}
                </Box>
              </Box>
            </TableCell>
          </TableRow>

          <TableRow>
            <TableCell sx={{ fontWeight: "medium" }}>
              <Box>
                <Typography variant="body2" fontWeight="medium">
                  Coupling (C)
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  quality
                </Typography>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <FormControl fullWidth size="small">
                    <Select
                      value={originData.coupling}
                      onChange={handleOriginSelectChange("coupling")}
                      sx={{ bgcolor: "white" }}
                    >
                      <MenuItem value="GOOD">Good</MenuItem>
                      <MenuItem value="FAIR">Fair</MenuItem>
                      <MenuItem value="POOR">Poor</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  CM = {formatMultiplier(results?.origin.multipliers.cm)}
                </Box>
              </Box>
            </TableCell>
            <TableCell sx={{ p: 0 }}>
              <Box display="flex">
                <Box flex={1} p={1} bgcolor="primary.50">
                  <FormControl fullWidth size="small">
                    <Select
                      value={destinationData.coupling}
                      onChange={handleDestinationSelectChange("coupling")}
                      sx={{ bgcolor: "white" }}
                    >
                      <MenuItem value="GOOD">Good</MenuItem>
                      <MenuItem value="FAIR">Fair</MenuItem>
                      <MenuItem value="POOR">Poor</MenuItem>
                    </Select>
                  </FormControl>
                </Box>
                <Box
                  flex={1}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bgcolor="grey.100"
                  fontWeight="medium"
                >
                  CM = {formatMultiplier(results?.destination.multipliers.cm)}
                </Box>
              </Box>
            </TableCell>
          </TableRow>

          {results && (
            <TableRow sx={{ bgcolor: "action.hover" }}>
              <TableCell sx={{ fontWeight: "bold" }}>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    Results
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    RWL & Lifting Index
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{ p: 2 }}>
                <Box textAlign="center">
                  <Typography variant="body2" fontWeight="medium">
                    <strong>RWL:</strong>{" "}
                    {formatResultWeight(results.origin.rwl)}
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mt={1}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      <strong>LI:</strong> {results.origin.liftingIndex}
                    </Typography>
                    <Chip
                      label={
                        results.origin.liftingIndex <= 1.0
                          ? "Safe"
                          : results.origin.liftingIndex <= 3.0
                            ? "Moderate"
                            : "High Risk"
                      }
                      color={
                        results.origin.liftingIndex <= 1.0
                          ? "success"
                          : results.origin.liftingIndex <= 3.0
                            ? "warning"
                            : "error"
                      }
                      size="small"
                    />
                  </Box>
                </Box>
              </TableCell>
              <TableCell sx={{ p: 2 }}>
                <Box textAlign="center">
                  <Typography variant="body2" fontWeight="medium">
                    <strong>RWL:</strong>{" "}
                    {formatResultWeight(results.destination.rwl)}
                  </Typography>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    gap={1}
                    mt={1}
                  >
                    <Typography variant="body2" fontWeight="medium">
                      <strong>LI:</strong> {results.destination.liftingIndex}
                    </Typography>
                    <Chip
                      label={
                        results.destination.liftingIndex <= 1.0
                          ? "Safe"
                          : results.destination.liftingIndex <= 3.0
                            ? "Moderate"
                            : "High Risk"
                      }
                      color={
                        results.destination.liftingIndex <= 1.0
                          ? "success"
                          : results.destination.liftingIndex <= 3.0
                            ? "warning"
                            : "error"
                      }
                      size="small"
                    />
                  </Box>
                </Box>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
